import React, { useMemo } from 'react';
import { useMachine } from '@xstate/react/lib';
import { assign, createMachine } from 'xstate';
import { AnyEventObject } from 'xstate/lib/types';
import { useDispatch } from 'react-redux';
import { useEpicDeps } from '@wearejh/m2-pwa-engine/lib/hooks/useEpicDeps';

import { useRouterLocationCausesRender } from 'src/hooks/router-hooks';

import { ConfigurableProductOptions } from './components/ConfigurableProductOptions';
import type {
    CleanConfigurableOptions,
    ConfigurableOptions,
    ConfigurableVariant,
    ConfigurableVariants,
    SelectedOptions,
} from './utils/cleanConfigurableProduct';
import { setConfigurableUrlParams, getSelectedVariant } from './utils';

type ConfigurableOptionsMachineProps = {
    configurableOptions?: CleanConfigurableOptions;
    selectedOptions?: SelectedOptions;
    selectedVariant?: ConfigurableVariant;
    setSelectedVariant: (product: ConfigurableVariant) => void;
};

type InitialContext = {
    options: ConfigurableOptions;
    selectedOptions: SelectedOptions;
    selectedVariant?: ConfigurableVariant;
    variants: ConfigurableVariants;
};

export function ConfigurableOptionsMachine(props: ConfigurableOptionsMachineProps) {
    const { pathname, search } = useRouterLocationCausesRender();
    const dispatch = useDispatch();
    const { replace } = useEpicDeps();

    /**
     * ConfigurableProductMachine is a memoized instance of a finite state machine that manages the behavior and state
     * of a configurable product options component.
     *
     * To summarise what the Machine handles:
     *
     * It first checks if url parameters exist and should be applied
     * It Then handles user interactions when clicking on configurable options.
     *
     * @typedef {Object} ConfigurableProductMachine
     *
     * @property {Function} ConfigurableProductMachine - UseMemo function that returns the ConfigurableProductMachine instance.
     *
     * @returns {StateMachine} - The created state machine instance.
     */
    const ConfigurableProductMachine = useMemo(() => {
        const initialContext: InitialContext = {
            options: props.configurableOptions?.configurable_options,
            selectedOptions: props.selectedOptions,
            selectedVariant: props.selectedVariant,
            variants: props.configurableOptions?.variants,
        };

        return createMachine(
            {
                context: initialContext,
                id: 'productOptions',
                initial: 'initializing',
                states: {
                    initializing: {
                        meta: 'State where the machine reads URL parameters to assign initial product options.',
                        always: {
                            target: 'ready',
                            actions: 'assignUrlParamsToSelectedOptions',
                        },
                    },
                    processing: {
                        meta: 'State where we process the selected option and update the URL.',
                        always: {
                            target: 'ready',
                            actions: 'assignUrlParamsToSelectedOptions',
                        },
                    },
                    ready: {
                        meta: 'State where the machine is ready for user interaction.',
                        on: {
                            SELECT_OPTION: {
                                target: 'processing',
                                actions: ['assignSelectedOption'],
                            },
                        },
                    },
                },
            },
            {
                actions: {
                    assignSelectedOption: assign((context, event: AnyEventObject) => {
                        const attribute = event.data.attributeCode;
                        const value = event.data.value;
                        const label = event.data.label;

                        /**
                         * If the value is truthy, add the selected option to the selectedOptions object.
                         */
                        if (value) {
                            return {
                                ...context,
                                selectedOptions: {
                                    ...context.selectedOptions,
                                    [attribute]: {
                                        value,
                                        label,
                                    },
                                },
                            };
                        }

                        /**
                         * If the value is falsy, remove the selected option from the selectedOptions object.
                         */
                        const selectedOptions = {
                            ...context.selectedOptions,
                        };

                        delete selectedOptions?.[attribute];

                        return {
                            ...context,
                            selectedOptions: {
                                ...selectedOptions,
                            },
                        };
                    }),
                    assignUrlParamsToSelectedOptions: (context) => {
                        /**
                         * When a user selects an option, update the URL with the selected options.
                         * This also include removing the selected option from the URL if the user deselects it.
                         */
                        const selectedOptions = context.selectedOptions;
                        const options = context.options;
                        const variants = context.variants;

                        const nextUrl = setConfigurableUrlParams(options, pathname, search, selectedOptions);
                        const selectedVariant = getSelectedVariant(variants, context.selectedOptions);

                        if (selectedVariant) {
                            props.setSelectedVariant(selectedVariant);
                        }

                        if (nextUrl) {
                            dispatch(replace(nextUrl));
                        }
                    },
                },
            },
        );
    }, [dispatch, pathname, props, replace, search]);

    const [state, send] = useMachine(ConfigurableProductMachine);
    const isReady = state.value === 'ready';
    const isProcessing = state.value === 'processing';

    if (!isReady) {
        return null;
    }

    return (
        <ConfigurableProductOptions
            variants={state.context.variants}
            options={state.context.options}
            selectedOptions={state.context.selectedOptions}
            isProcessing={isProcessing}
            send={send}
        />
    );
}
